import type { CreateProviderInput, ProviderType } from '@emailforge/schemas';
import { Button, Chip, Input, Select } from '@emailforge/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  useCreateProviderMutation,
  useDeleteProviderMutation,
  useListProvidersQuery,
  useSetDefaultProviderMutation,
  useVerifyProviderMutation,
} from '@/features/providers/providersApi';
import { t } from '@/lib/i18n';

type ProviderForm = {
  providerType: ProviderType;
  displayName: string;
  apiKey: string;
  serverToken: string;
  host: string;
  port: number;
  username: string;
  password: string;
  secure: boolean;
  domain: string;
  region: string;
  accessKeyId: string;
  secretAccessKey: string;
};

const blankForm: ProviderForm = {
  providerType: 'resend',
  displayName: 'Production',
  apiKey: '',
  serverToken: '',
  host: '',
  port: 587,
  username: '',
  password: '',
  secure: true,
  domain: '',
  region: 'us-east-1',
  accessKeyId: '',
  secretAccessKey: '',
};

export function SMTPScreen() {
  const navigate = useNavigate();
  const { data: providers, isLoading } = useListProvidersQuery();
  const [createProvider, { isLoading: isCreating }] = useCreateProviderMutation();
  const [verifyProvider, { isLoading: isVerifying }] = useVerifyProviderMutation();
  const [setDefault] = useSetDefaultProviderMutation();
  const [deleteProvider] = useDeleteProviderMutation();
  const [form, setForm] = useState<ProviderForm>(blankForm);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      const body = buildInput(form);
      const res = await createProvider(body).unwrap();
      await verifyProvider(res.id).unwrap();
      setShowForm(false);
      setForm(blankForm);
    } catch (err) {
      setError(
        (err as { data?: { message?: string } }).data?.message ?? (err as Error).message ?? 'Failed to connect',
      );
    }
  };

  return (
    <>
      <TopBar
        crumbs={[t('crumbs.configure'), 'SMTP providers']}
        right={
          !showForm ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" onClick={() => navigate('/app/smtp/onboard')}>
                <Icon.zap size={13} /> Auto-onboard
              </Button>
              <Button variant="flame" size="sm" onClick={() => setShowForm(true)}>
                <Icon.plus size={13} /> Connect provider
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          )
        }
      />
      <div style={{ flex: 1, padding: 24, overflowY: 'auto', background: 'var(--paper-2)' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          {showForm ? (
            <ConnectForm
              form={form}
              setForm={setForm}
              onSubmit={submit}
              isLoading={isCreating || isVerifying}
              error={error}
            />
          ) : (
            <>
              <h1 className="display" style={{ fontSize: 32, margin: '0 0 8px', letterSpacing: -0.015 }}>
                SMTP providers
              </h1>
              <p style={{ color: 'var(--ink-2)', fontSize: 14, margin: '0 0 20px' }}>
                We send through your SMTP provider — you keep ownership of your deliverability reputation. No lock-in.
              </p>

              {isLoading && (
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Loading providers…
                </div>
              )}

              {!isLoading && (providers ?? []).length === 0 && (
                <div className="mf-card" style={{ padding: 40, textAlign: 'center' }}>
                  <div
                    className="mono"
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.08,
                      color: 'var(--ink-3)',
                    }}
                  >
                    No providers connected
                  </div>
                  <h2 className="display" style={{ fontSize: 22, margin: '10px 0 8px' }}>
                    Connect your first provider
                  </h2>
                  <p style={{ fontSize: 13.5, color: 'var(--ink-2)', maxWidth: 440, margin: '0 auto 20px' }}>
                    We support Resend and SendGrid in MVP. Others (Postmark, SES, Mailgun) ship in v1.2.
                  </p>
                  <Button variant="flame" onClick={() => setShowForm(true)}>
                    <Icon.plus size={13} /> Connect provider
                  </Button>
                </div>
              )}

              {(providers ?? []).length > 0 && (
                <div
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}
                >
                  {providers?.map((p) => (
                    <div
                      key={p.id}
                      className="mf-card"
                      style={{ padding: 18, borderColor: p.isDefault ? 'var(--flame)' : undefined }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, textTransform: 'capitalize' }}>
                          {p.providerType.replace('_', ' ')}
                        </div>
                        {p.isDefault && (
                          <Chip variant="flame">default
                          </Chip>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 6 }}>{p.displayName}</div>
                      <div
                        className="mono"
                        style={{
                          fontSize: 10.5,
                          color: 'var(--ink-3)',
                          textTransform: 'uppercase',
                          letterSpacing: 0.06,
                          marginBottom: 14,
                        }}
                      >
                        <Chip
                          variant={
                            p.healthStatus === 'healthy'
                              ? 'live'
                              : p.healthStatus === 'down'
                                ? 'err'
                                : 'draft'
                          }
                        >
                          {p.healthStatus}
                        </Chip>
                        {p.verifiedAt && (
                          <span style={{ marginLeft: 8 }}>
                            verified {new Date(p.verifiedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button variant="ghost" size="sm" onClick={() => verifyProvider(p.id)} disabled={isVerifying}>
                          Verify
                        </Button>
                        {!p.isDefault && (
                          <Button variant="ghost" size="sm" onClick={() => setDefault(p.id)}>
                            Make default
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={async () => {
                            if (window.confirm('Delete this provider connection?')) {
                              await deleteProvider(p.id).unwrap();
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function ConnectForm({
  form,
  setForm,
  onSubmit,
  isLoading,
  error,
}: {
  form: ProviderForm;
  setForm: (f: ProviderForm) => void;
  onSubmit: () => void;
  isLoading: boolean;
  error: string | null;
}) {
  const patch = (p: Partial<ProviderForm>) => setForm({ ...form, ...p });

  return (
    <div className="mf-card" style={{ padding: 28, maxWidth: 560 }}>
      <h2 className="display" style={{ fontSize: 22, margin: '0 0 6px' }}>
        Connect an SMTP provider
      </h2>
      <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 18 }}>
        Credentials are envelope-encrypted (AES-256-GCM) at rest with a tenant-scoped key.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <Field label="Provider">
          <Select
            
            value={form.providerType}
            onChange={(e) => patch({ providerType: e.target.value as ProviderType })}>
            <option value="resend">Resend</option>
            <option value="sendgrid">SendGrid</option>
            <option value="smtp_generic">SMTP (generic)</option>
            <option value="postmark" disabled>
              Postmark (Phase 3)
            </option>
            <option value="ses" disabled>
              Amazon SES (Phase 3)
            </option>
            <option value="mailgun" disabled>
              Mailgun (Phase 3)
            </option>
          </Select>
        </Field>

        <Field label="Display name">
          <Input  value={form.displayName} onChange={(e) => patch({ displayName: e.target.value })} />
        </Field>

        {(form.providerType === 'resend' || form.providerType === 'sendgrid') && (
          <Field label={`${form.providerType === 'resend' ? 'Resend' : 'SendGrid'} API key`}>
            <Input mono
              
              value={form.apiKey}
              onChange={(e) => patch({ apiKey: e.target.value })}
              placeholder={form.providerType === 'resend' ? 're_...' : 'SG....'} />
          </Field>
        )}

        {form.providerType === 'smtp_generic' && (
          <>
            <Field label="Host">
              <Input  value={form.host} onChange={(e) => patch({ host: e.target.value })} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Port">
                <Input
                  
                  type="number"
                  value={form.port}
                  onChange={(e) => patch({ port: Number(e.target.value) })} />
              </Field>
              <Field label="Secure (TLS)">
                <Select
                  
                  value={form.secure ? 'true' : 'false'}
                  onChange={(e) => patch({ secure: e.target.value === 'true' })}>
                  <option value="true">TLS</option>
                  <option value="false">STARTTLS / none</option>
                </Select>
              </Field>
            </div>
            <Field label="Username">
              <Input  value={form.username} onChange={(e) => patch({ username: e.target.value })} />
            </Field>
            <Field label="Password">
              <Input
                
                type="password"
                value={form.password}
                onChange={(e) => patch({ password: e.target.value })} />
            </Field>
          </>
        )}

        {error && (
          <div
            style={{
              background: 'var(--err-soft)',
              color: 'var(--err)',
              padding: '10px 12px',
              borderRadius: 'var(--r-2)',
              fontSize: 12.5,
              border: '1px solid oklch(0.85 0.10 25)',
            }}
          >
            {error}
          </div>
        )}

        <Button variant="flame" type="submit" disabled={isLoading} style={{ marginTop: 6 }}>
          {isLoading ? 'Connecting…' : 'Connect and verify'}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label
        className="mono"
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          color: 'var(--ink-3)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function buildInput(form: ProviderForm): CreateProviderInput {
  const common = { providerType: form.providerType, displayName: form.displayName };
  switch (form.providerType) {
    case 'resend':
      return { ...common, providerType: 'resend', apiKey: form.apiKey };
    case 'sendgrid':
      return { ...common, providerType: 'sendgrid', apiKey: form.apiKey };
    case 'postmark':
      return { ...common, providerType: 'postmark', serverToken: form.serverToken };
    case 'mailgun':
      return {
        ...common,
        providerType: 'mailgun',
        apiKey: form.apiKey,
        domain: form.domain,
        region: (form.region as 'us' | 'eu') ?? 'us',
      };
    case 'ses':
      return {
        ...common,
        providerType: 'ses',
        accessKeyId: form.accessKeyId,
        secretAccessKey: form.secretAccessKey,
        region: form.region,
      };
    case 'smtp_generic':
      return {
        ...common,
        providerType: 'smtp_generic',
        host: form.host,
        port: form.port,
        username: form.username,
        password: form.password,
        secure: form.secure,
      };
  }
}
